import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { useConversation } from '@elevenlabs/react';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { supabase } from '@/lib/supabase';
import { useGemBalance } from '@/hooks/useGemBalance';
import { CHAT_EVENTS, useTracking } from '@/hooks/useTracking';
import type { Database } from '@/types/database';
import { getDisplayName } from '@/lib/utils';

type Character = Database['eh']['Tables']['characters']['Row'];

export const GEMS_PER_MINUTE = 5;
export const MAX_CALL_DURATION_MINUTES = 120;

type CallState = 'idle' | 'pre-call' | 'requesting_url' | 'connecting' | 'connected' | 'ended' | 'error';

interface CallContextType {
  callState: CallState;
  forceOpenManager: boolean;
  preCall: (character: Character, chatId: string) => void;
  startCall: () => void;
  endCall: () => void;
  character: Character | null;
  chatId: string | null;
  errorMessage: string;
  elapsedTime: number;
  initialBalance: number;
  showLowBalanceWarning: boolean;
  remainingCallTime: number;
  resetCall: () => void;
}

const CallContext = createContext<CallContextType | undefined>(undefined);

export function CallProvider({ children }: { children: ReactNode }) {
  const { session } = useSessionContext();
  const { balance, refreshBalance } = useGemBalance();
  const { trackEvent } = useTracking();
  const [character, setCharacter] = useState<Character | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  
  const [callState, setCallState] = useState<CallState>('idle');
  const [forceOpenManager, setForceOpenManager] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastMessage, setLastMessage] = useState<{ role: 'user' | 'ai', content: string } | null>(null);
  const voiceIdRef = useRef<string | null>(null);
  const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  const [initialBalance, setInitialBalance] = useState(0);
  const [showLowBalanceWarning, setShowLowBalanceWarning] = useState(false);
  const [remainingCallTime, setRemainingCallTime] = useState(0);

  const endCallCallback = useCallback(async (elapsedTime: number) => {
    if (callStateRef.current !== 'ended') {
      setCallState('ended');
      trackEvent(CHAT_EVENTS.CHAT_CALL_ENDED, {
        characterId: character?.id,
        chatId,
        elapsedMinutes: Math.round(elapsedTime / 60),
        voiceId: voiceIdRef.current,
        userId: session?.user.id,
      });
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      await new Promise(resolve => setTimeout(resolve, 15000)); // wait for post-call webhook to complete
      await refreshBalance();
    }
  }, [character?.id, chatId, refreshBalance, session?.user.id, trackEvent]);

  const callStateRef = useRef(callState);
  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  const handleError = (error: string, { showErrorState = false } = {}) => {
    setErrorMessage(`Sorry, an error occurred during your call${character ? ` with ${getDisplayName(character)}` : ''}.${
      showErrorState ? ' Please try again later.' : ''
    }`);
    if (showErrorState) setCallState('error');
    trackEvent(CHAT_EVENTS.CHAT_CALL_ERROR, {
      error: typeof error === 'string' ? error : JSON.stringify(error),
      characterId: character?.id,
      chatId,
      balance: balance.total,
      elapsedMinutes: Math.round(elapsedTime / 60),
      userId: session?.user.id,
      voiceId: voiceIdRef.current,
    });
  };

  const conversation = useConversation({
    onConnect: (_) => {
      if (import.meta.env.DEV) console.log('conversation.onConnect event received');
      setCallState('connected');
      trackEvent(CHAT_EVENTS.CHAT_CALL_STARTED, {
        characterId: character?.id,
        chatId,
        voiceId: voiceIdRef.current,
        userId: session?.user.id,
      });
    },
    onMessage: (message) => {
      if (import.meta.env.DEV) console.log('conversation.onMessage event received', message);
      setLastMessage({ role: message.source, content: message.message });
    },
    onDisconnect: (details) => {
      if (import.meta.env.DEV) console.log('conversation.onDisconnect event received', details);
      // User balance won't be debited if the call was not started
      if (details.reason === 'error' && !lastMessage?.content) {
        handleError(details.message, { showErrorState: true });
        return;
      } else if (details.reason === 'error') {
        // don't show error state for longer calls (assuming some conversation could take place)
        // but just show some message to explain the unexpected end of call
        handleError(details.message, { showErrorState: false });
      }

      if (elapsedTime > (MAX_CALL_DURATION_MINUTES - 1) * 60 && !errorMessage) {
        // error message without error state to just show an explanation for the unexpected end of call
        setErrorMessage(`Calls are limited to ${MAX_CALL_DURATION_MINUTES} minutes.`);
      }
      endCallCallback(elapsedTime);
    },
    onError: (error) => {
      handleError(error);
    },
  });

  useEffect(() => {
    if (callState === 'connected') {
      callTimerRef.current = setInterval(() => {
        setElapsedTime(prevElapsedTime => {
          const newElapsedTime = prevElapsedTime + 1;
          const currentCost = (newElapsedTime / 60) * GEMS_PER_MINUTE;
          const remainingBalance = initialBalance - currentCost;

          if (import.meta.env.DEV && newElapsedTime % 10 === 0) console.log('CallContext.useEffect 1 second interval refresh', newElapsedTime, currentCost, remainingBalance);
          
          if (remainingBalance <= 5) {  
            setShowLowBalanceWarning(true);
            const remainingTimeInSeconds = Math.max(0, (remainingBalance / GEMS_PER_MINUTE) * 60);
            setRemainingCallTime(remainingTimeInSeconds);
          } else {
            setShowLowBalanceWarning(false);
          }

          if (remainingBalance <= 0) {
            conversation.endSession();
          }
          return newElapsedTime;
        });
      }, 1000);
    } else {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    }
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    };
  }, [callState, initialBalance, conversation]);

  const preCall = useCallback((char: Character, chId: string) => {
    const changedCharacters = char.id !== character?.id;
    if (['requesting_url', 'connecting', 'connected'].includes(callState)) {
      setForceOpenManager(true);
      if (changedCharacters) alert('Call already in progress. Please end the current call before starting a new one.');
      return;
    };
    setCharacter(char);
    setChatId(chId);
    setCallState('pre-call');
  }, [callState, character]);

  const startCall = useCallback(async () => {
    if (!character || !chatId || !session?.user) return;
    setInitialBalance(balance.total);
    setShowLowBalanceWarning(false);
    setElapsedTime(0);
    setCallState('requesting_url');
    try {
      const { data, error } = await supabase.functions.invoke('eh-start-call', {
        body: { characterId: character.id, chatId: chatId },
      });

      if (error) throw new Error(error.message);

      setCallState('connecting');
      voiceIdRef.current = data.voiceId;

      // MARK: adjust the dynamic variables defaults based on the system prompt they will be injected into
      await conversation.startSession({
        signedUrl: data.signedUrl,
        dynamicVariables: { // all properties are required
          user_id: session.user.id,
          user_name: data.userName || ' ',
          user_age: data.userAge || 'unknown',
          user_gender: data.userGender || 'unknown gender',
          chat_summary: data.chatSummary || 'no chat history',
          chat_id: chatId,
          character_id: character.id,
          character_aboutme: character.aboutme || 'mysterious background',
          character_age: character.age || 'unknown age',
          character_body_type: character.body_type || 'normal',
          character_ethnicity: character.ethnicity || 'unknown ethnicity',
          character_eye_color: character.eye_color || 'unknown eye color',
          character_gender: character.gender === 'm' ? 'male' : (character.gender === 'f' ? 'female' : 'unknown gender'),
          character_hair_color: character.hair_color || 'unknown hair color',
          character_hobbies: character.hobbies.join(', ') || 'unknown',
          character_kinks: character.kinks.join(', ') || 'unknown',
          character_occupation: character.occupation || 'unknown',
          character_name: getDisplayName(character) || 'unknown name', // for safety, but all characters should have a name
          // character_last_name: character.last_name || 'unknown last name',
          character_personality: character.personality || 'unknown personality',
          character_relationship: character.relationship || 'unknown relationship',
          last_messages: data.lastMessages || 'no messages',
          last_message_date: data.lastMessageDate || 'N/A',
        },
        overrides: {
          ...(data.firstMessage ? { agent: { firstMessage: data.firstMessage } } : {}),
          tts: {
            voiceId: data.voiceId,
          }
        }
      });
    } catch (err) {
      const error = err as Error;
      trackEvent(CHAT_EVENTS.CHAT_CALL_ERROR, {
        error: typeof error === 'string' ? error : JSON.stringify(error),
        characterId: character?.id,
        chatId,
        balance: balance.total,
        elapsedMinutes: 0,
        userId: session?.user.id,
        voiceId: voiceIdRef.current,
      });
      setErrorMessage('An error occurred during the call. Please try again later.');
      setCallState('error');
    }
  }, [character, chatId, session, balance.total, conversation, trackEvent]);

  const endCall = useCallback(() => {
    conversation.endSession();
    // endCallCallback will be called by onDisconnect
  }, [conversation]);
  
  const resetCall = useCallback(() => {
    setForceOpenManager(false);
    setCallState('idle');
    setElapsedTime(0);
    setErrorMessage('');
    setLastMessage(null);
    setShowLowBalanceWarning(false);
    setCharacter(null);
    setChatId(null);
  }, []);


  const value = {
    callState,
    forceOpenManager,
    preCall,
    startCall,
    endCall,
    character,
    chatId,
    errorMessage,
    elapsedTime,
    initialBalance,
    showLowBalanceWarning,
    remainingCallTime,
    resetCall,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export const useCall = () => {
  const context = useContext(CallContext);
  if (context === undefined) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
}; 